import { create } from "zustand";
import type { CardUnion } from "@/components/generative-ui/envelope/types";

const CAP = 10;

export interface CanvasCardEntry {
  card: CardUnion;
  pinned: boolean;
  lastInteractedAt: number | null;
}

export interface CanvasState {
  entries: CanvasCardEntry[];
  push: (card: CardUnion) => void;
  dismiss: (cardId: string) => void;
  pin: (cardId: string) => void;
  unpin: (cardId: string) => void;
  setActive: (cardId: string) => void;
  clear: () => void;
}

function dedupeKey(card: CardUnion): string {
  return `id:${card.card_id}`;
}

export const useCardCanvasStore = create<
  CanvasState & {
    cards: ReadonlyArray<CardUnion & { pinned: boolean; lastInteractedAt: number | null }>;
    activePersonaId: string | null;
  }
>((set, get) => {
  const project = (entries: CanvasCardEntry[]) =>
    entries.map((e) => ({ ...e.card, pinned: e.pinned, lastInteractedAt: e.lastInteractedAt }));

  const deriveActive = (entries: CanvasCardEntry[]) => {
    const interacted = entries
      .filter((e) => e.lastInteractedAt !== null)
      .sort((a, b) => (b.lastInteractedAt ?? 0) - (a.lastInteractedAt ?? 0));

    const lastPersona = interacted.find((e) => e.card.kind === "persona_builder");

    return {
      activePersonaId:
        lastPersona && lastPersona.card.kind === "persona_builder"
          ? (lastPersona.card.persona_id ?? null)
          : null,
    };
  };

  const commit = (entries: CanvasCardEntry[]) =>
    set({ entries, cards: project(entries), ...deriveActive(entries) });

  return {
    entries: [],
    cards: [],
    activePersonaId: null,

    push: (card) => {
      const key = dedupeKey(card);
      const prev = get().entries;
      const withoutDupe = prev.filter((e) => dedupeKey(e.card) !== key);
      const next: CanvasCardEntry[] = [
        { card, pinned: false, lastInteractedAt: null },
        ...withoutDupe,
      ];

      let nonPinnedCount = 0;
      const pruned = next.filter((e) => {
        if (e.pinned) return true;
        nonPinnedCount += 1;
        return nonPinnedCount <= CAP;
      });

      commit(pruned);
    },

    dismiss: (cardId) => {
      commit(get().entries.filter((e) => e.card.card_id !== cardId));
    },

    pin: (cardId) => {
      commit(get().entries.map((e) => (e.card.card_id === cardId ? { ...e, pinned: true } : e)));
    },

    unpin: (cardId) => {
      commit(get().entries.map((e) => (e.card.card_id === cardId ? { ...e, pinned: false } : e)));
    },

    setActive: (cardId) => {
      const now = Date.now();
      commit(
        get().entries.map((e) =>
          e.card.card_id === cardId ? { ...e, lastInteractedAt: now } : e,
        ),
      );
    },

    clear: () => commit([]),
  };
});
